import React from "react";
import Select from "react-select";

/** An entity doc as the options carry it; only `_id` is read here. */
export type EntityDoc = { _id?: unknown };

export type EntityOption = { label: string; value: EntityDoc };

export type EntitySelectFieldProps = {
  settingVar: { variable: string; value?: EntityDoc | EntityDoc[] | null };
  options?: EntityOption[];
  onChange: (name: string, value: EntityDoc | EntityDoc[]) => void;
  isMulti?: boolean;
  orderFromValue?: boolean;
};

/**
 * Entity-picker building block (SCRUM-552, VariableField registry).
 *
 * One multi-select for the five entity-reference types — Product, Category,
 * Subcategory, Ad and Blog. The only per-type difference is the `options`
 * list, injected by the host screen (label/value pairs built from redux).
 * Replaces 5 types × host screens of copies of the same dropdown.
 *
 * Contract: reports the clean value via `onChange(name, value)`. In the
 * default multi mode value is the array of full entity docs (option.value),
 * identical to the legacy onChangeValueDropDownComplex payload.
 *
 * Prefill, written properly once: an array value matches by `_id`; a legacy
 * non-array value (single doc from old data) matches that one doc — the
 * superset of the two slightly different legacy variants (HandleRequired
 * supported single docs, HandleOptional showed nothing for them).
 *
 * Content mode (SCRUM-641): `isMulti={false}` for the content-side entity
 * variables, which store ONE full doc (the settings side stores arrays).
 * Single mode prefills by `_id` and reports the selected doc itself —
 * identical to the legacy onChangeSingleValueDropDown payload of the update
 * screens and the wizard's e.value branch. Not clearable (react-select
 * default), like every current single select.
 *
 * `orderFromValue` (SCRUM-643 review fix): the default multi derivation
 * groups the chips in OPTION-LIST order, which a controlled Select then
 * writes back into the stored array on the next change. The wizard's legacy
 * Selects were uncontrolled and preserved CLICK order — its host opts in to
 * derive the chips from the stored value's own order so the persisted array
 * order stays what the editor picked. The settings side and the update
 * screens keep the default (options-order is their pinned behaviour).
 */
export default function EntitySelectField({
  settingVar,
  options = [],
  onChange,
  isMulti = true,
  orderFromValue = false,
}: EntitySelectFieldProps) {
  const value = settingVar.value;
  const selected = Array.isArray(value)
    ? orderFromValue
      ? value
          .map((v) => options.find((option) => option.value?._id === v?._id))
          .filter((option): option is EntityOption => Boolean(option))
      : options.filter((option) => value.some((v) => v?._id === option.value._id))
    : options.filter((option) => option.value?._id === value?._id);

  if (!isMulti) {
    return (
      <Select
        classNamePrefix="select"
        value={selected[0] ?? null}
        name={settingVar.variable}
        options={options}
        // Not clearable, so react-select never hands null here; `sel!` keeps
        // the old unguarded `sel.value` rather than adding a guard.
        onChange={(sel) => onChange(settingVar.variable, sel!.value)}
        isSearchable={true}
      />
    );
  }

  return (
    <Select
      classNamePrefix="select"
      value={selected}
      name={settingVar.variable}
      options={options}
      onChange={(sel) =>
        onChange(settingVar.variable, (sel ?? []).map((s) => s.value))
      }
      isSearchable={true}
      isMulti
    />
  );
}
